import React, { useContext } from 'react';

import type {
  WorkflowAiCandidate,
  WorkflowAiResult,
  WorkflowAiToolCallResult,
} from '@aline/common';
import { getWorkflowAiLifecycleState, WorkflowAiResultSchema } from '@aline/common';
import { link } from '@aline/routes';
import { ButtonBase, Link, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { MentionEntityContext } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { getWorkflowAiBuildStatusLabel } from 'src/components/workflow/workflow-detail/workflow-ai-build-state';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { useChatThreadId } from '../../ChatPanelMarkdown';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  cardAction: {
    '&:hover': {
      borderColor: theme.appColors.primary,
    },
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'block',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
    textAlign: 'left',
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.short,
    }),
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  header: {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: 0,
    textAlign: 'left',
    width: '100%',
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.25),
  },
  row: {
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
  },
  rowMeta: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: 11,
    textTransform: 'capitalize',
  },
  rowTitle: {
    fontSize: 13,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(0.5),
  },
  status: {
    color: theme.appColors.primary,
    fontSize: 12,
    fontWeight: theme.typography.fontWeightBold,
  },
  statusChip: {
    alignItems: 'center',
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    height: 16,
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
  },
  summary: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  title: {
    color: theme.palette.text.secondary,
    flexGrow: 1,
    fontSize: 12,
  },
  workflowCardHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  workflowCardIcon: {
    alignItems: 'center',
    color: theme.appColors.primary,
    display: 'flex',
    flexShrink: 0,
  },
  workflowCardMain: {
    minWidth: 0,
  },
  workflowCardSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(3),
  },
  workflowCardTitle: {
    color: theme.palette.text.primary,
    fontSize: 16,
    fontWeight: 700,
  },
  workflowCardTitleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
}));

export const CodeWorkflowElement = React.memo(function CodeWorkflowElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(
    codeBlockInfo.code,
    WorkflowAiResultSchema,
    'CodeWorkflowElement'
  );
  if (!parsed.ok) {
    return (
      <div className={classes.root}>
        <div className={classes.content}>
          <Typography className={classes.title}>Workflow result unavailable</Typography>
          <Typography className={classes.summary}>
            Could not display workflow result. The data may be malformed.
          </Typography>
        </div>
      </div>
    );
  }
  if (parsed.data.status === 'launched') return null;
  if (parsed.data.status === 'created_draft') return <WorkflowResult result={parsed.data} />;

  return (
    <div className={classes.root}>
      <WorkflowResult result={parsed.data} />
    </div>
  );
});

function WorkflowResult({ result }: { result: WorkflowAiResult }) {
  const { classes } = useStyles();
  const candidates = getWorkflowResultCandidates(result);
  if (candidates) return <WorkflowResultCandidateList candidates={candidates} result={result} />;
  if (result.status === 'created_draft' || result.status === 'launched')
    return <WorkflowResultBody result={result} />;

  return (
    <div className={classes.content}>
      <Typography className={classes.title}>{getWorkflowResultTitle(result)}</Typography>
      <Typography className={classes.summary}>{result.reasoningSummary}</Typography>
      <WorkflowResultBody result={result} />
    </div>
  );
}

export function WorkflowResultCandidateList({
  candidates,
  defaultExpanded = true,
  result,
  title = getWorkflowResultTitle(result),
}: {
  candidates: WorkflowAiCandidate[];
  result: WorkflowAiResult | WorkflowAiToolCallResult;
  defaultExpanded?: boolean;
  title?: string;
}) {
  const { classes } = useStyles();
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  return (
    <div className={classes.content}>
      <ButtonBase className={classes.header} onClick={() => setExpanded(value => !value)}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Typography className={classes.title}>{title}</Typography>
      </ButtonBase>
      {expanded ? (
        <div className={classes.rows}>
          {candidates.map(candidate => (
            <div key={candidate.workflowId} className={classes.row}>
              <Typography className={classes.rowTitle}>
                <WorkflowLink title={candidate.title} url={candidate.url} />
              </Typography>
              <Typography className={classes.rowMeta}>{candidate.kind}</Typography>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function WorkflowResultBody({ result }: { result: WorkflowAiResult }) {
  switch (result.status) {
    case 'created_draft':
    case 'launched': {
      return <CreatedWorkflowResultBody result={result} />;
    }
    case 'errored':
      return <Typography color="error">{result.errorMessage}</Typography>;
    case 'duplicates_found':
    case 'no_match':
    case 'template_candidates':
      return null;
  }
}

function CreatedWorkflowResultBody({
  result,
}: {
  result: Extract<WorkflowAiResult, { status: 'created_draft' | 'launched' }>;
}) {
  const { classes } = useStyles();
  const mentionEntityContext = useContext(MentionEntityContext);
  const threadId = useChatThreadId();
  const workflowId = result.workflow.workflowId;
  const { data } = Apollo.useGetWorkflowSimpleQuery({ variables: { workflowId } });
  const mentionedWorkflow = mentionEntityContext.workflows.get(workflowId);
  const workflow = data?.workflow;
  const title =
    workflow?.name ||
    (mentionedWorkflow?.__typename === 'Workflow'
      ? mentionedWorkflow.name || 'Untitled Workflow'
      : 'Workflow');
  const buildState = workflow ? getWorkflowAiLifecycleState(workflow) : 'idle';
  const status = getWorkflowAiBuildStatusLabel(buildState);
  const statusChipColors = getWorkflowResultStatusColors(status);
  const entityLabel = getWorkflowResultEntityLabel({ result, workflow });
  const url =
    result.status === 'created_draft'
      ? link.WorkflowDetailTabThread({ threadId, workflowId })
      : link.WorkflowDetailTabSummary({ workflowId });
  const building = buildState === 'building';
  const heading = getCreatedWorkflowResultHeading({ building, entityLabel });
  const handleOpenWorkflow = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      openLink(url, { title });
    },
    [title, url]
  );

  return (
    <div className={classes.cardAction} onClick={handleOpenWorkflow}>
      <div className={classes.workflowCardMain}>
        <div className={classes.workflowCardHeader}>
          <div className={classes.workflowCardTitleRow}>
            <div className={classes.workflowCardIcon}>
              {building ? (
                <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
              ) : (
                <Sparkles size={16} />
              )}
            </div>
            <Typography className={classes.workflowCardTitle}>{heading}</Typography>
          </div>
          <div
            className={classes.statusChip}
            style={{ background: statusChipColors.chipColor, color: statusChipColors.textColor }}
          >
            {status}
          </div>
        </div>
        <Typography className={classes.workflowCardSubtitle}>
          Aline created &quot;
          <WorkflowLink title={title} url={url} />
          &quot;.
        </Typography>
      </div>
    </div>
  );
}

function getWorkflowResultEntityLabel({
  result,
  workflow,
}: {
  result: Extract<WorkflowAiResult, { status: 'created_draft' | 'launched' }>;
  workflow: Apollo.GetWorkflowSimpleQuery['workflow'] | null | undefined;
}) {
  if (result.status === 'created_draft' && workflow?.variant === Apollo.WorkflowVariant.Template)
    return 'Template';

  return 'Workflow';
}

function getCreatedWorkflowResultHeading({
  building,
  entityLabel,
}: {
  building: boolean;
  entityLabel: 'Template' | 'Workflow';
}) {
  return building ? `Creating ${entityLabel}...` : `Created ${entityLabel}`;
}

function getWorkflowResultStatusColors(status: ReturnType<typeof getWorkflowAiBuildStatusLabel>) {
  switch (status) {
    case 'Building':
      return { chipColor: '#E0F2FE', textColor: '#0086C9' };
    case 'Ready':
      return { chipColor: '#D1FADF', textColor: '#039855' };
    case 'Needs attention':
      return { chipColor: '#FEE4E2', textColor: '#D91F11' };
    case 'Cancelled':
      return { chipColor: '#EEEEEE', textColor: '#767676' };
  }
}

export function getWorkflowResultCandidates(
  result: WorkflowAiResult | WorkflowAiToolCallResult
): WorkflowAiCandidate[] | null {
  switch (result.status) {
    case 'duplicates_found':
    case 'no_match':
    case 'template_candidates':
      return result.candidates.length ? result.candidates : null;
    case 'created_draft':
    case 'errored':
    case 'launched':
      return null;
  }
}

/**
 * Renders a link that opens the workflow in an in-app Aline tab on left-click
 * (via openLink -> the tab system's CustomEvent), while preserving the
 * native `href` so right-click / middle-click "open in new tab" still work.
 */
function WorkflowLink({ title, url }: { title: string; url: string }) {
  return (
    <Link
      href={url}
      onClick={event => {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        openLink(url, { title });
      }}
    >
      {title}
    </Link>
  );
}

function getWorkflowResultTitle(result: WorkflowAiResult | WorkflowAiToolCallResult) {
  switch (result.status) {
    case 'duplicates_found':
      return 'Found existing workflows';
    case 'template_candidates':
      return 'Found workflow templates';
    case 'launched':
      return 'Launched workflow';
    case 'created_draft':
      return 'Created draft workflow';
    case 'no_match':
      return 'No matching workflow found';
    case 'errored':
      return 'Workflow tool failed';
  }
}
